// role.service.ts
import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { eps } from './url.service'
import { AssignedTo, Role } from '../store/role/role.state'
import { PlanModule } from '../components/plans/plans.component'
import { User } from '../store/auth/auth.state'
type PermissionAction = 'status' | 'view' | 'add' | 'edit' | 'delete'

// interface ApiModule {
//   id: string
//   name: string
//   permissions: { action: string }[]
// }

interface ApiPermissionPayload {
  moduleId: string
  permissionIds: string[]
}

export interface ApiPermission {
  id: string
  action: string
  moduleId?: string
}

export interface ApiModule {
  id: string
  name: string
  permissions: ApiPermission[]
  adminOnly?: boolean
  scopeType?: string
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  private apiUrl = eps.role.roleApi

  constructor(private http: HttpClient) {}

  getRoles(params?: string | null, page?: number, limit?: number): Observable<any> {
    const url = params
      ? `${this.apiUrl}?${params}&page=${page}&limit=${limit}`
      : `${this.apiUrl}?page=${page}&limit=${limit}`
    return this.http.get<any>(url)
  }

  modules(): Observable<{ modules: ApiModule[] }> {
    return this.http.get<{ modules: ApiModule[] }>(eps.role.modulesApi)
  }

  createRole(role: {
    name: string
    // permissions: Record<string, PermissionAction[]> // 👈 better typing
    permissions: ApiPermissionPayload[]
    isStandard: boolean
    businessIds: []
  }): Observable<Role> {
    return this.http.post<Role>(`${this.apiUrl}`, role)
  }

  updateRole(role: {
    id: string
    name: string
    // permissions: Record<string, PermissionAction[]> // 👈 better typing
    permissions: ApiPermissionPayload[]

    isStandard: boolean
    businessId: string
  }): Observable<Role> {
    return this.http.put<Role>(`${this.apiUrl}/${role.id}`, role)
  }

  getRoleById(id: string): Observable<Role> {
    return this.http.get<Role>(`${this.apiUrl}/${id}`)
  }

  getAssignedTo(roleId: string): Observable<AssignedTo[]> {
    return this.http.get<AssignedTo[]>(`${this.apiUrl}/assignedTo/${roleId}`)
  }

  createAssignedTo(
    roleId: string,
    userData: { userId: string }[],
    businessId?: string | null
  ): Observable<AssignedTo[]> {
    return this.http.post<AssignedTo[]>(`${this.apiUrl}/assignedTo`, {
      roleId,
      userData,
      businessId
    })
  }

  upsertValidUpto(id: string, validUpto: Date | null): Observable<AssignedTo[]> {
    return this.http.put<AssignedTo[]>(`${this.apiUrl}/assignedTo/${id}`, { validUpto })
  }

  deleteAssignedTo(id: string): Observable<AssignedTo[]> {
    return this.http.delete<AssignedTo[]>(`${this.apiUrl}/assignedTo/${id}`)
  }

  deleteRole(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, {})
  }

  modulesWithDependency(): Observable<{ modules: PlanModule[] }> {
    return this.http.get<{ modules: PlanModule[] }>(`${eps.role.modulesApi}/with-dependencies`)
  }

  getUnssignedUsers(
    roleId: string,
    businessId: string | null,
    businessIdtoAssign: string | null
  ): Observable<{ users: User[]; totalCount: number }> {
    let params = new URLSearchParams()

    if (businessId) {
      params.append('businessId', businessId)
    }

    if (businessIdtoAssign) {
      params.append('businessIdtoAssign', businessIdtoAssign)
    }

    return this.http.get<{ users: User[]; totalCount: number }>(
      `${this.apiUrl}/unassignedUser/${roleId}?${params.toString()}`
    )
  }
}
